import {
  getTimeSortFieldFromStatusFilter,
  getUniqueIdOfSearchResultAndType,
  joinTagArray,
  splitTagPath,
  shouldUseFifoOrder,
} from './util';
import { parseSortOption } from './constants/sortOptions';
import { handleError } from 'services/ErrorService/ErrorService';
import values from 'object.values';
import last from 'lodash/last';
import uniq from 'lodash/uniq';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import { createSelector } from 'reselect';
import * as SimplrUserAuthorization from '@asurion-core/typescript-utils/SimplrUserAuthorization';
import { IDENTIFIER_TYPES, DEFAULT_IDENTIFIER_TYPE } from './constants/identifierTypes';
import { isLiveChat } from 'util/chat';
import { isEscalatableProvider } from './util/ticketSelector';
import * as searchTypes from './constants/searchTypes';
import { DEFAULT_FIRST_GREETING, DEFAULT_FORBIDDEN_TEXT, DEFAULT_SECOND_GREETING } from './constants/fixedMessages';
import { SPECIALIST_LITE } from './constants/expertClassTypes';
import { CONVERSATION_QUERY_LIMIT } from './constants/limits';
import * as DIRECTIONS from './constants/messageDirection';
import { alertsTypesList, ESCALATION, ESCALATION_PARTNER } from 'constants/alerts';
import * as tabIndexes from './constants/ui';
import { getExpertId } from './util/parseSelectors';
import {
  getTreeAtPath,
  getBreadcrumbTitleOfNode,
  checkIfMacroIsNew,
  sortMacrosByUsage,
  isExpiredMacro,
} from './util/decisionTreeUtil';
import { NOMINATED_TICKET_STATUSES, TICKET_STATUS_ASSIGNED } from 'constants/ticketStatuses';
import { trackedFields as trackedMacroWizardFields } from './reducers/tools/macroWizard';
import * as MessageTypes from './constants/messageTypes';
import * as macroTypes from 'constants/macroTypes';
import { BLOCK_DAILY, BLOCK_WEEKLY, BLOCK_ONCE, ALWAYS_BLOCK, READ, GUIDE } from 'constants/alerts';
import { DraftHelpers } from './util/Draft';
import { MACRO_WIZARD, SEARCH_ALL } from 'constants/searchTypes';
import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';
import { appendHTTPSProtocalIfNeeded } from './util/url';
import * as conversationTypes from 'constants/conversationTypes';
import * as listTypes from 'constants/infoPanelTypes';
import includes from 'lodash/includes';
import { _createSelector } from 'util/selectorUtils';
import first from 'lodash/first';
import { ALL, ASSIGNED, LIVE_CHAT, NOMINATED } from 'constants/tabNames';
import { INBOUND } from './constants/messageDirection';
import { DEFAULT_CONVERSATION_LANGUAGE_CODE } from 'constants/llmTranslation';

/*
helper methods for extracting information from the state
  */
export const extractSelectedHistoricalTicketIdFromState = (state) => state.selectedHistoricalTicketId;

export const extractNonHistoricalTicketIdFromState = (state) => state.selectedTicketId;

export const getTickets = (state) => state.tickets;

export const getReviewTickets = (state) => state.tickets && state.tickets.reviewTickets;

export const extractNominatedTickets = (state) => state.tickets && state.tickets.nominatedTickets;

export const extractAssignedTickets = (state) => state.tickets && state.tickets.assignedTickets;

export const extractAssignedTicketsArray = (state) =>
  state.tickets.assignedTickets.allTickets.map((id) => state.tickets.assignedTickets[id]);

export const getAllTickets = (state) => state.tickets && state.tickets.allTickets;

export const extractHistoricalTickets = (state) => state.tickets && state.tickets.historicalTickets;

export const extractSelectedInboxTab = (state) => state.ui.messages.selectedInboxTab;

export const isSpecialist = (state) => state.user && state.user.isAdmin;

export const isSuperAdmin = (state) => state.user && state.user.isSuperAdmin;

export const isAPartnerProtector = (state) => state.user?.isAPartnerProtector;

export const isAccountUser = (state) => state.user?.isAccountUser;

export const isSpecialistLite = (state) => get(state, 'user.expertClass', undefined) === SPECIALIST_LITE;

export const getWhiteList = (state) => get(state, 'user.whitelist', []);

export const getPartnersAllowlist = (
  state,
  resourceName = SimplrUserAuthorization.POLICY_RESOURCES.CONVERSATION_MODEL_READ
) => {
  const userPolicies = get(state, 'user.userPolicies', []);

  const policy = SimplrUserAuthorization.findAllowedPolicyForResource(userPolicies, resourceName);

  return policy?.partnersAllowlist || [];
};

export const getAllowedPolicyForResource = (state, resourceName) => {
  const userPolicies = get(state, 'user.userPolicies', []);

  return SimplrUserAuthorization.findAllowedPolicyForResource(userPolicies, resourceName);
};

export const getUser = (state) => state.user;

export const getUserId = (state) => get(state, 'user.id', null);

export const getAllBusinessesObject = (state) => state.businesses && state.businesses.all;

function InconsistentStateError(message) {
  this.name = 'InconsistentStateError';
  this.message = message;
}

InconsistentStateError.prototype = Error.prototype;

export const extractSelectedTicketLongTransactionStatus = (state, ticketId) => {
  if (state.longTransactions === undefined) {
    return undefined;
  }

  return state.longTransactions[ticketId];
};

export const extractCenterPaneInfoPanelActiveFromState = (state) => state.ui.centerPane.extraInfoPanelActive;

export const extractInfoPanelListTypeFromState = (state) => state.ui.centerPane.listType;

export const extractCenterPaneSelectedOrderFromState = (state) => state.ui.centerPane.selectedOrder;

export const extractCenterPaneSelectedSubscriptionFromState = (state) => state.ui.centerPane.selectedSubscription;

export const extractSelectedTicketIdFromState = _createSelector(
  [extractNonHistoricalTicketIdFromState, extractSelectedHistoricalTicketIdFromState],
  (nonHistoricalTicketIdFromState, selectedHistoricalTicketIdFromState) => {
    if (selectedHistoricalTicketIdFromState) {
      return selectedHistoricalTicketIdFromState;
    }

    return nonHistoricalTicketIdFromState;
  }
);

// This is true when a previous/historical conversation is being displayed
export const extractDisplayingHistoricalConversationFromState = (state) => {
  return state.ui.displayingHistoricalConversation;
};

export const extractSelectedHistoricalTicketFromState = _createSelector(
  [extractHistoricalTickets, extractSelectedHistoricalTicketIdFromState],
  (historicalTickets, selectedHistoricalTicketId) => {
    return get(historicalTickets, selectedHistoricalTicketId, null);
  }
);

export const extractSelectedNonHistoricalTicketForAllTab = (state) => {
  const ticketId = extractNonHistoricalTicketIdFromState(state);
  const ticketQueue = getTicketQueueFromTab(state, 'all');

  return ticketQueue[ticketId];
};

export const toolsMaskingActive = _createSelector(
  [
    extractCenterPaneInfoPanelActiveFromState,
    extractSelectedHistoricalTicketIdFromState,
    extractDisplayingHistoricalConversationFromState,
  ],
  (centerPanelActive, historicalTicketId, isDisplayingHistoricalConversation) => {
    return centerPanelActive || !!historicalTicketId || isDisplayingHistoricalConversation;
  }
);

export const getTicketQueueFromTab = (state, tabId) => {
  if (extractSelectedHistoricalTicketIdFromState(state)) {
    return state.tickets.historicalTickets;
  }
  // TODO: Add default case
  // eslint-disable-next-line
  switch (tabId) {
    case 'nominated':
      return state.tickets.nominatedTickets;
    case 'assigned':
    case 'live_chat':
      return state.tickets.assignedTickets;
    case 'all':
      return state.tickets.allTickets;
  }
};

export const isTicketAssigned = (state, ticketId) => {
  const ticket = extractTicketFromStateById(state, ticketId);
  const currentExpertId = getExpertId();

  if (!ticket) {
    return false;
  }

  const ticketIsAssigned = ticket.ticketStatus === TICKET_STATUS_ASSIGNED;
  const ticketIsAssignedToExpert = ticket.expertId === currentExpertId;

  return ticketIsAssigned && ticketIsAssignedToExpert;
};

export const isTicketAlreadyInQueue = (state, tabId, ticketId) => {
  const isAdmin = isSpecialist(state);
  const queue = getTicketQueueFromTab(state, tabId);
  const ticketInAssignedTabs = ['assigned', 'live_chat'].includes(tabId);
  const ticket = queue[ticketId];

  if (isAdmin || !ticketInAssignedTabs || !ticket) {
    return !!ticket;
  }

  // assigned and live_chat tickets share a queue
  if (isLiveChat(ticket.communicationType) && tabId === 'assigned') {
    return false;
  } else if (!isLiveChat(ticket.communicationType) && tabId === 'live_chat') {
    return false;
  }

  return true;
};

// Returns true if the oldest tickets should be on top
export const useFifoOrderingForTab = (state, tabId) => {
  // experts always use lifo
  if (!isSpecialist(state)) {
    return false;
  }
  if (tabId === 'all') {
    const ticketStatus = state.selectedTicketStatus;

    // forward to experts, escalated, and awaiting_nomination use fifo
    return shouldUseFifoOrder(ticketStatus);
  } else if (tabId === 'nominated') {
    // nominated means escalated for specialists
    return true;
  }

  // default is false
  return false;
};

/** Given the state and a tab id, return the object that has the parameters for ordering tickets
 * @param  {ReduxState} state
 * @param  {String} tabId
 * @returns {Object}
 */
export const getSortParamsForTab = (state, tabId) => {
  if (tabId === ALL) {
    const customSort = parseSortOption(state.appliedSortOrder);
    if (customSort) {
      return {
        timeSortField: customSort.field,
        useFifoOrder: customSort.direction === 'ASC',
      };
    }
  }

  const ticketStatus = state.selectedTicketStatus;
  // eslint-disable-next-line
  const useFifoOrder = useFifoOrderingForTab(state, tabId);
  const timeSortField = getTimeSortFieldFromStatusFilter(ticketStatus);

  return {
    useFifoOrder,
    timeSortField,
  };
};

// Return true if the all tab has CONVERSATION_QUERY_LIMIT or more tickets
export const isTabFull = (state, tabId) => {
  const ticketQueue = getTicketQueueFromTab(state, tabId);

  return ticketQueue && ticketQueue.allTickets.length >= CONVERSATION_QUERY_LIMIT;
};

export const extractSelectedTicketFromState = _createSelector(
  [
    extractSelectedHistoricalTicketIdFromState,
    extractNonHistoricalTicketIdFromState,
    extractSelectedHistoricalTicketFromState,
    getReviewTickets,
    extractNominatedTickets,
    extractAssignedTickets,
    getAllTickets,
  ],
  (
    selectedHistoricalTicketId,
    selectedTicketId,
    selectedHistoricalTicket,
    reviewTickets,
    nominatedTickets,
    assignedTickets,
    allTickets
  ) => {
    if (selectedHistoricalTicketId) {
      return selectedHistoricalTicket;
    }

    if (selectedTicketId) {
      if (reviewTickets[selectedTicketId]) {
        return reviewTickets[selectedTicketId];
      } else if (nominatedTickets[selectedTicketId]) {
        return nominatedTickets[selectedTicketId];
      } else if (assignedTickets[selectedTicketId]) {
        return assignedTickets[selectedTicketId];
      } else if (allTickets[selectedTicketId]) {
        return allTickets[selectedTicketId];
      }
    }

    // set to return undefined, some functions will default it to an empty object
    return undefined;
  }
);

export const getSelectedTicketSubject = (state) => {
  return get(extractSelectedTicketFromState(state), 'subject');
};

export const getSelectedTicketCustomerEmail = (state) => {
  return get(extractSelectedTicketFromState(state), 'customerEmail');
};

export const getSelectedTicketCommunicationType = (state) => {
  return get(extractSelectedTicketFromState(state), 'communicationType');
};

export const extractSelectedNonHistoricalTicketFromState = _createSelector(
  [
    extractNonHistoricalTicketIdFromState,
    getReviewTickets,
    extractNominatedTickets,
    extractAssignedTickets,
    getAllTickets,
  ],
  (selectedTicketId, reviewTickets, nominatedTickets, assignedTickets, allTickets) => {
    if (!selectedTicketId) {
      return;
    }
    if (reviewTickets[selectedTicketId]) {
      return reviewTickets[selectedTicketId];
    } else if (nominatedTickets[selectedTicketId]) {
      return nominatedTickets[selectedTicketId];
    } else if (assignedTickets[selectedTicketId]) {
      return assignedTickets[selectedTicketId];
    } else if (allTickets[selectedTicketId]) {
      return allTickets[selectedTicketId];
    }
  }
);

export const extractTicketFromStateById = (state, id) => {
  if (state.tickets.reviewTickets[id]) {
    return state.tickets.reviewTickets[id];
  } else if (state.tickets.nominatedTickets[id]) {
    return state.tickets.nominatedTickets[id];
  } else if (state.tickets.assignedTickets[id]) {
    return state.tickets.assignedTickets[id];
  } else if (state.tickets.allTickets[id]) {
    return state.tickets.allTickets[id];
  }

  return null;
};

export const getSelectedQueue = (state) => {
  // TODO: Add default case
  // eslint-disable-next-line
  switch (state.ui.messages.selectedInboxTab) {
    case 'nominated':
      return state.tickets.nominatedTickets;
    case 'assigned':
    case 'live_chat':
      return state.tickets.assignedTickets;
    case 'all':
      return state.tickets.allTickets;
  }
};

export const getSelectedTicketBrand = (state) => {
  return get(extractSelectedTicketFromState(state), 'brand');
};

export const getRecommendedMLTagsForSelectedTicket = (state) => {
  const selectedTab = extractSelectedInboxTab(state);
  const selectedTicket = extractSelectedTicketFromState(state);

  if (isEmpty(selectedTicket)) {
    return [];
  }

  const simplrCategories = extractSimplrTagsForSelectedTicket(state);

  const selectedBrandCategories = get(simplrCategories, `brandCategories.${selectedTicket.brand}`, []);
  const commonBrandCategories = get(simplrCategories, `brandCategories.common`, []);
  const allBrandCategories = uniq([...selectedBrandCategories, ...commonBrandCategories]);

  let inScopeTags, allSimplrTags;

  if (isEmpty(allBrandCategories)) {
    inScopeTags = extractInScopeTagsForSelectedTicket(state);
    allSimplrTags = extractAllSimplrTagsForSelectedTicketAsArray(state);
  } else {
    inScopeTags = allSimplrTags = allBrandCategories;
  }

  const ticketHasBeenTaken = selectedTab === 'assigned' || selectedTab === 'live_chat';
  if (selectedTicket && selectedTicket.recommendedMLTags) {
    if (ticketHasBeenTaken) {
      return selectedTicket.recommendedMLTags.filter((tag) => inScopeTags.includes(tag));
    }

    return selectedTicket.recommendedMLTags.filter((tag) => allSimplrTags.includes(tag));
  }

  return [];
};

export const getToDisplayRecommendedMLTagsForSelectedTicket = (state) => {
  const selectedTicket = extractSelectedTicketFromState(state);

  return selectedTicket && selectedTicket.shouldDisplayRecommendedMLTags;
};

export const getRequestDetailsRecommendedMLTagsForSelectedTicket = (state) => {
  const selectedTicket = extractSelectedTicketFromState(state);

  return selectedTicket && selectedTicket.recommendedMLTagsRequestDetails;
};

export const getMultiIntentClassification = (state) => {
  const selectedTicket = extractSelectedTicketFromState(state);
  if (selectedTicket && selectedTicket.multiIntentClassification) {
    return selectedTicket.multiIntentClassification;
  }

  return MessageTypes.SINGLE_INTENT; // default behavior.
};

export const getBrandOfSelectedTicket = (state) => {
  const ticket = extractSelectedTicketFromState(state);

  return ticket ? ticket.brand : null;
};

const getUnSentMessages = (state) => state.ui.unSentMessages;

export const getSelectedUnSentMessage = createSelector(
  [extractSelectedTicketFromState, getUnSentMessages],
  (selectedTicket, unSentMessages) => {
    if (selectedTicket) {
      return unSentMessages[selectedTicket.ticketId] || {};
    }

    return {};
  }
);

export function extractSelectedTicketBusinessDetailsFieldValueFromState(state, fieldName) {
  if (!fieldName) {
    return null;
  }
  const extraBusinessInfo = getExtraBusinessInfoOfSelectedTicket(state);

  return extraBusinessInfo && extraBusinessInfo[fieldName];
}

// returns Business Details for partner of selected ticket
export const extractSelectedTicketBusinessDetailsFromState = _createSelector(
  [extractSelectedTicketFromState, getAllBusinessesObject],
  (selectedTicket, allBusinesses) => {
    const simplrAccountId = selectedTicket?.simplrAccountPtrId;

    if (!simplrAccountId) {
      return {};
    }

    return allBusinesses?.[simplrAccountId] || {};
  }
);

export const extractSimplrTagsForSelectedTicket = createSelector(
  [extractSelectedTicketBusinessDetailsFromState],
  (businessDetail) => businessDetail?.simplrCategories || {}
);

export const extractInScopeTagsForSelectedTicket = (state) => {
  const { categoryRules } = extractSimplrTagsForSelectedTicket(state);
  const inScopeTags = [];
  if (categoryRules) {
    for (let tag in categoryRules) {
      if (categoryRules[tag]) {
        inScopeTags.push(tag);
      }
    }
  }

  return inScopeTags;
};

export const extractAllSimplrTagsForSelectedTicketAsArray = (state) => {
  const { categoryRules } = extractSimplrTagsForSelectedTicket(state);

  return categoryRules ? Object.keys(categoryRules) : [];
};

export const extractOwnAvailability = (state) => get(state.experts.idsToExperts[state.user.id], 'availability');

const ticketComparisonFunction = (ticket1, ticket2) => {
  const ticket1OpenedAt = ticket1['conversationOpenedAt'] ? ticket1['conversationOpenedAt'] : ticket1['createdAt'];
  const ticket2OpenedAt = ticket2['conversationOpenedAt'] ? ticket2['conversationOpenedAt'] : ticket2['createdAt'];

  if (ticket2OpenedAt < ticket1OpenedAt) {
    return 1;
  } else if (ticket2OpenedAt > ticket1OpenedAt) {
    return -1;
  }

  return 0;
};

export const moveUnreadExpertAssignedTicketsToTop = (assignedtickets) => {
  if (!Array.isArray(assignedtickets)) {
    return [];
  }

  let unReadTickets = assignedtickets
    .filter((ticket) => {
      return ticket.unreadCount > 0;
    })
    .sort(ticketComparisonFunction);

  let readTickets = assignedtickets
    .filter((ticket) => {
      return ticket.unreadCount === 0;
    })
    .sort(ticketComparisonFunction);

  const newAssignedTickets = unReadTickets.concat(readTickets);

  return newAssignedTickets;
};

export const extractTickets = (selectedInboxTab, ticketsStateObject, _isSpecialist) => {
  const assignedTickets = ticketsStateObject.assignedTickets.allTickets.map(
    (id) => ticketsStateObject.assignedTickets[id]
  );
  switch (selectedInboxTab) {
    case 'nominated':
      return ticketsStateObject.nominatedTickets.allTickets.map((id) => ticketsStateObject.nominatedTickets[id]);
    case 'assigned':
      return _isSpecialist
        ? moveUnreadExpertAssignedTicketsToTop(assignedTickets)
        : moveUnreadExpertAssignedTicketsToTop(
            assignedTickets.filter(({ communicationType }) => !isLiveChat(communicationType))
          );

    case 'live_chat':
      if (_isSpecialist) {
        throw new Error('specialist does not have live chat queue');
      } else {
        return moveUnreadExpertAssignedTicketsToTop(
          assignedTickets.filter(({ communicationType }) => isLiveChat(communicationType))
        );
      }
    case 'all':
      return ticketsStateObject.allTickets.allTickets.map((id) => ticketsStateObject.allTickets[id]);

    default:
      throw new Error(`unknown selectedInboxTab ${selectedInboxTab}`);
  }
};

export const extractAssignedChats = (state) => {
  const assignedTickets = state.tickets.assignedTickets.allTickets.map((id) => state.tickets.assignedTickets[id]);

  return assignedTickets.filter(({ communicationType }) => isLiveChat(communicationType));
};

export const extractAssignedEmails = (state) => {
  const assignedTickets = state.tickets.assignedTickets.allTickets.map((id) => state.tickets.assignedTickets[id]);

  return assignedTickets.filter(({ communicationType }) => !isLiveChat(communicationType));
};

const getDefaultAssignedTicket = _createSelector(
  [extractAssignedTickets, isSpecialist],
  (assignedTickets, _isSpecialist) => {
    const assignedTicketsArray = assignedTickets.allTickets.map((id) => assignedTickets[id]);
    const nonLiveChatTicketsArray = assignedTicketsArray.filter(
      ({ communicationType }) => !isLiveChat(communicationType)
    );

    return first(
      _isSpecialist
        ? assignedTicketsArray.map(({ ticketId }) => ticketId)
        : nonLiveChatTicketsArray.map(({ ticketId }) => ticketId)
    );
  }
);

const getDefaultLiveChatTicket = _createSelector([extractAssignedTickets], (assignedTickets) => {
  const assignedTicketsArray = assignedTickets.allTickets.map((id) => assignedTickets[id]);
  const liveChatTickets = assignedTicketsArray.filter(({ communicationType }) => isLiveChat(communicationType));

  return first(liveChatTickets.map(({ ticketId }) => ticketId));
});

const getDefaultTicket = _createSelector(
  [
    extractSelectedInboxTab,
    extractNominatedTickets,
    extractAssignedTickets,
    getDefaultLiveChatTicket,
    getDefaultAssignedTicket,
  ],
  (selectedInboxTab, nominatedTickets, assignedTickets, defaultLiveChatTicket, defaultAssignedTicket) => {
    switch (selectedInboxTab) {
      case NOMINATED:
        return first(nominatedTickets.allTickets);
      case ASSIGNED:
        return defaultAssignedTicket;
      case LIVE_CHAT:
        return defaultLiveChatTicket;
      case ALL:
        return first(assignedTickets.allTickets);
      default:
        throw new Error(`unknown selectedInboxTab ${selectedInboxTab}`);
    }
  }
);

export const extractTicketsFromState = _createSelector(
  [extractSelectedTicketIdFromState, extractSelectedInboxTab, getTickets, isSpecialist, getDefaultTicket],
  (selectedTicketIdFromState, selectedInboxTab, tickets, _isSpecialist, defaultTicket) => {
    const addSelected = (ticket) => ({ ...ticket, selected: ticket.ticketId === selectedTicketId });

    const selectedTicketId = selectedTicketIdFromState || defaultTicket;

    return extractTickets(selectedInboxTab, tickets, _isSpecialist).map(addSelected);
  }
);

export const extractCurrentQueueLength = (state) => extractTicketsFromState(state).length;

export const extractFirstMessage = (state) => {
  const { conversation } = state;
  const firstMessageId = conversation.allMessages[0];

  return conversation[firstMessageId];
};

export const extractFirstCustomerMessage = (state) => {
  const { conversation } = state;
  for (let i = 0; i < conversation.allMessages.length; i++) {
    const messageId = conversation.allMessages[i];
    const message = conversation[messageId];
    if (message.direction === DIRECTIONS.INBOUND) {
      return message;
    }
  }
};

export const getLengthOfSelectedResultList = (state) => {
  const resultList = getSelectedResultList(state);

  return resultList && resultList.length;
};

// TODO: Move to tools selector file
export const getSearchTypeOfSelectedAction = (state) => {
  return state.tools.general.searchType;
};

export const getAlertManagerType = (state) => {
  return state.tools.alertsManager.alertManagerType;
};

export const getShowToolsState = (state) => {
  return state.ui.tools.general.showTools;
};

export const getSelectedSearchResultIndex = (state) => {
  return state.tools.ticketSearch.selectedResult.resultIndex;
};

export const getSelectedSearchResultType = (state) => {
  return state.tools.ticketSearch.selectedResult.resultType;
};

export function getCurrentShopifyOrderFromSubscription(state) {
  return state.tools.subscriptionOrders.orders.currentShopifyOrder;
}

export const getSelectedSubscriptionForConfirmationPage = (state) => {
  return state.tools.subscriptionSearch.selectedSubscriptionForConfirmationPage;
};

export const getSelectedOrderFromOrdersSearch = (state) => {
  return state.tools.ordersSearch.selectedOrder || {};
};

export const getSelectedItemsFromOrdersSearch = (state) => {
  return get(state, 'tools.ordersSearch.selectedItems', {});
};

export const getAllOrdersFromOrdersSearch = (state) => {
  return get(state, 'tools.ordersSearch.orders', []);
};

export const getSelectedNestedSubItems = (state) => {
  return get(state, `tools.ordersSearch.selectedNestedSubItems`, null);
};

export const getRbiRefundCounts = (state) => {
  return get(state, `tools.ordersSearch.rbiRefundsCount`, null);
};

export const isSelectedShopifyOrderRefunded = (state) => {
  const order = getSelectedOrderFromOrdersSearch(state);

  return order.refunds && order.refunds.length > 0;
};

export const getSelectedResultList = (state) => {
  const { macroResults, kbResults, ticketResults, partnerPlatformHistoricalTickets } = state.tools.ticketSearch;
  const { SEARCH_TICKETS, SEARCH_MACROS, SEARCH_KB, SEARCH_PARTNER_PLATFORM_HISTORICAL_TICKETS } = searchTypes;
  const resultType = getSelectedSearchResultType(state);

  switch (resultType) {
    case SEARCH_MACROS:
      return macroResults;
    case SEARCH_KB:
      return kbResults;
    case SEARCH_TICKETS:
      return ticketResults;
    case SEARCH_PARTNER_PLATFORM_HISTORICAL_TICKETS:
      return partnerPlatformHistoricalTickets;
    default:
      return [];
  }
};

export const getSelectedSearchResult = (state) => {
  const resultIndex = getSelectedSearchResultIndex(state);
  if (resultIndex < 0) {
    return {};
  }
  const resultList = getSelectedResultList(state);

  return resultList[resultIndex];
};

export const canEscalateCurrentTicket = (state, ticket = null) => {
  ticket = ticket || extractSelectedTicketFromState(state);
  if (!ticket) {
    return false;
  }
  const {
    conversation,
    user: { isAdmin },
  } = state;
  const escalatableWithinSimplr = !isAdmin;
  const escalatableOutsideSimplr =
    isAdmin &&
    conversation &&
    conversation.allMessages.length &&
    (ticket.escalateToSmbEmail || isEscalatableProvider(ticket));

  return escalatableWithinSimplr || escalatableOutsideSimplr;
};

export const isSelectedTicketLiveChat = (state) => {
  const ticket = extractSelectedTicketFromState(state);

  // extractSelectedTicketFromState can return undefined so check ticket for being falsy
  return isLiveChat(ticket && ticket.communicationType);
};

/**
 * Returns true if an expert or specialist has replied to the current conversation
 * @param {object} state
 * @returns {boolean}
 */
export const hasExpertReplied = (state) => {
  const { conversation } = state;

  return conversation.allMessages.some((messageId) => {
    const message = conversation[messageId];

    return message.direction === 'OUTBOUND' && message.source === 'SIMPLR';
  });
};

/**
 * Returns true if the last message with direction of OUTBOUND or INBOUND, was sent by the currently assigned expert
 * @param {object} state
 * @returns {boolean}
 */
export const doesAssignedExpertHaveLastReplyToSelectedConversation = (state) => {
  const { conversation } = state;
  const currentExpertId = getExpertId();

  const lastPublicMessageId = conversation.allMessages.toReversed().find((messageId) => {
    const message = conversation[messageId];

    return [DIRECTIONS.OUTBOUND, DIRECTIONS.INBOUND].includes(message.direction);
  });
  const isLastMessageFromCurrentExpert = conversation[lastPublicMessageId]?.expert?.simplrExpertId === currentExpertId;
  // NOTE(dave|SPD-11417|2024-12-16): the expert object is based on the expert that was assigned when the message was
  // created, not necessarily who created the message. For example on INBOUND messages from a customer, we assign the
  // simplrExpertPtr of the current expert.
  const isLastMessageOutbound = conversation[lastPublicMessageId]?.direction === DIRECTIONS.OUTBOUND;

  return isLastMessageFromCurrentExpert && isLastMessageOutbound;
};

/**
 * Returns true if the conversation selected has the same id as the selected ticket
 * @param {object} state
 * @returns {boolean}
 */
export const isSelectedConversationLoaded = (state) => {
  const { selectedConversationId } = state.conversation;
  const ticketId = extractSelectedTicketIdFromState(state);

  return ticketId === selectedConversationId;
};

export const getSmbOfSelectedTicket = (state) => {
  const ticket = extractSelectedTicketFromState(state) || {};

  return ticket.smbName;
};

export const isSearchResultDeleted = (state, result, resultType) => {
  const uniqueId = getUniqueIdOfSearchResultAndType(result, resultType);

  return !!state.tools.ticketSearch.deletedResultsById[uniqueId];
};

export const isSelectedTicketTaken = (state) => {
  const selectedInboxTab = extractSelectedInboxTab(state);

  return selectedInboxTab === tabIndexes.ASSIGNED_TAB || selectedInboxTab === tabIndexes.LIVE_CHAT_TAB;
};

export const getHomepageOfSelectedTicket = (state) => {
  const business = extractSelectedTicketBusinessDetailsFromState(state);
  const homepageUrl = business.homepageUrl;
  const homepageUrls = business.homepageUrls;
  const ticket = extractSelectedTicketFromState(state);
  if (!homepageUrls || !ticket || !ticket.brand) {
    return appendHTTPSProtocalIfNeeded(homepageUrl);
  }
  // Expected object homepageUrls :
  // {"parentUrl": "www.rbi.com", "brandUrls": [{"brandId": 12334, "brandName": "Popeyes", "url": "www.popeyes.com"}]}
  const parentUrl = homepageUrls.parentUrl;
  const smbBrandsUrlsInfo = homepageUrls.brandUrls || [];
  const ticketbrandInfo = smbBrandsUrlsInfo.filter(
    (brandInfo) => brandInfo.brandName && brandInfo.brandName.toLowerCase().trim() === ticket.brand.toLowerCase().trim()
  )[0];
  const ticketbrandUrl = ticketbrandInfo && ticketbrandInfo.url;

  return appendHTTPSProtocalIfNeeded(ticketbrandUrl || parentUrl || homepageUrl);
};

export const getPartnerInfoOfSelectedTicket = _createSelector(
  [extractSelectedTicketBusinessDetailsFromState],
  (business) => business?.partnerInfo || []
);

export const getExtraBusinessInfoOfSelectedTicket = createSelector(
  [extractSelectedTicketBusinessDetailsFromState],
  (business) => business?.extraBusinessInfo || {}
);

export const getRefundReasonOptions = createSelector(
  [getExtraBusinessInfoOfSelectedTicket],
  (extraBusinessInfo) => extraBusinessInfo?.refundReasonOptions || []
);

export const getGreyOutFeatureEnabled = createSelector(
  [getExtraBusinessInfoOfSelectedTicket],
  (extraBusinessInfo) => extraBusinessInfo?.greyOutFeatureEnabled || false
);

export const getCancelOrRefundReasonOptions = createSelector(
  [getExtraBusinessInfoOfSelectedTicket],
  (extraBusinessInfo) => extraBusinessInfo?.cancelRefundReasonOptions || []
);

export const getCouponReasonOptions = createSelector(
  [getExtraBusinessInfoOfSelectedTicket],
  (extraBusinessInfo) => extraBusinessInfo?.couponReasonOptions || []
);

export const getActionReasons = (state) => {
  return get(state, 'tools.ordersSearch.actionReasons', null);
};

export const getPointsPrimaryReason = createSelector([getActionReasons], (actionReasons) =>
  get(actionReasons, 'pointsPrimaryReason', '')
);

export const getSelectedRbiItemsForRefund = (state) => {
  return get(state, 'tools.ordersSearch.selectedRbiItemsForRefund', null);
};

export const getSelectedRbiItemsForCoupon = (state) => {
  return get(state, 'tools.ordersSearch.selectedRbiItemsForCoupon', null);
};

export const getSelectedRbiItemsForLoyaltyPoints = (state) => {
  return get(state, 'tools.ordersSearch.selectedRbiItemsForLoyaltyPoints', null);
};

export const getRefundAmountDetails = (state) => {
  return get(state, 'tools.ordersSearch.refundAmountDetails', null);
};

export const getRbiApprovedRefundAmount = (state) => {
  return get(state, 'tools.ordersSearch.refundAmountDetails.approvedAmountCents', null);
};

export const getStuckInTransitThresholdOfSelectedTicket = (state) => {
  const extraBusinessInfo = getExtraBusinessInfoOfSelectedTicket(state) || {};

  return extraBusinessInfo.stuckInTransitThreshold;
};

export const getRecentDeliveryThreshold = (state) => {
  const extraBusinessInfo = getExtraBusinessInfoOfSelectedTicket(state);

  return extraBusinessInfo.recentDeliveryThreshold;
};

export const getRecentOrderThreshold = (state) => {
  const extraBusinessInfo = getExtraBusinessInfoOfSelectedTicket(state);

  return extraBusinessInfo.recentOrderThreshold;
};

export const getResponseGuideUrlOfSelectedTicket = (state) => {
  const extraBusinessInfo = extractSelectedTicketBusinessDetailsFromState(state);

  return extraBusinessInfo.responseGuideUrl;
};

export const checkPrePopulatedResponseConfig = (state) => {
  const extraBusinessInfo = getExtraBusinessInfoOfSelectedTicket(state) || {};
  const selectedTicket = extractSelectedTicketFromState(state); // selected ticket is undefined for historical ticekts
  if (!selectedTicket) {
    return false;
  }

  // don't add response if livechat
  return !!extraBusinessInfo.prepopulateExpertResponse && !isLiveChat(selectedTicket.communicationType);
};

export const getNewMessagesEditor = (state, ticketId = state.selectedTicketId) => {
  const unSentTextInfo = state.ui.unSentMessages[ticketId] || {};
  const greeting = getPrePopulatedResponse(state);
  // editorState is now the message content as an HTML string.
  if (unSentTextInfo.editorState) {
    let html = unSentTextInfo.editorState;
    const plainText = DraftHelpers.getPlainText(html);
    if (greeting && !plainText) {
      html = DraftHelpers.appendHtmlToContents(greeting, html);
    }

    return html;
  }

  return DraftHelpers.appendHtmlToContents(greeting || '', '');
};

export const getNewMessagesCouponCodeType = (state) => {
  const unSentTextInfo = state.ui.unSentMessages[state.conversation.selectedConversationId] || {};

  return unSentTextInfo.macroCouponCodeTypeId;
};

export const getPrePopulatedResponse = (state) => {
  // check config

  if (!checkPrePopulatedResponseConfig(state)) {
    return '';
  }

  // default greeting for specialsts and experts
  let text = hasExpertReplied(state) ? DEFAULT_SECOND_GREETING : DEFAULT_FIRST_GREETING;
  // only add forbidden for first message for experts
  if (!isSpecialist(state) && !hasExpertReplied(state)) {
    text += DEFAULT_FORBIDDEN_TEXT;
  }

  return text;
};

export const getLastSuccessfulMessageFromSimplr = (state) => {
  const allMessages = getAllMessages(state);

  const simplrMessages = allMessages.filter(
    (message) => message.source === 'SIMPLR' && message.direction === 'OUTBOUND' && message.status !== 'ERROR'
  );

  return simplrMessages.length > 0 ? last(simplrMessages).messageBody : undefined;
};

export const checkSmbSpelling = (state) => {
  const extraBusinessInfo = getExtraBusinessInfoOfSelectedTicket(state) || {};

  return !!extraBusinessInfo.checkSmbSpelling;
};

export const showingMacroWizard = (state) => {
  const searchType = getSearchTypeOfSelectedAction(state);

  return searchType === searchTypes.MACRO_WIZARD;
};

export const canMacroWizardCanGoBack = (state) => {
  if (!showingMacroWizard(state)) {
    return false;
  }
  const macroWizardState = extractMacroWizardObject(state);
  // iteratate through the tracked fields
  // if any field has a truthy value, we can go back
  for (let field of trackedMacroWizardFields) {
    if (macroWizardState[field]) {
      return true;
    }
  }

  return false;
};

export const selectAllFilters = (state) => state.allFilters;

export const getSelectedBusinessIdList = (state) => state.allFilters?.businessIds || [];

export const getSelectedBusinessBrandOptionsList = (state) => state.allFilters?.selectedBrandOptions || [];

export const getBusinessBrandOptions = createSelector(
  [(state) => state.businesses.all, getSelectedBusinessIdList],
  (allBusinesses, selectedBusinessIds) => {
    return selectedBusinessIds.reduce((acc, id) => {
      return { ...acc, ...allBusinesses[id]?.brandOptions };
    }, {});
  }
);

export const excludeSelectedBusinessInFilter = (state) => state.allFilters?.excludeBusinesses || false;

export const getDateRange = (state) => {
  return state.allFilters?.dateRange || { startDate: null, endDate: null, startTime: null, endTime: null };
};

export const getIdentifier = (state) => state.allFilters?.identifier || { type: DEFAULT_IDENTIFIER_TYPE, value: '' };

export const getAllFilterSimplrTicketNumber = (state) => {
  const identifier = getIdentifier(state);

  return identifier.type === IDENTIFIER_TYPES.SIMPLR_TICKET ? identifier.value : '';
};

export const getAllFilterWidgetId = (state) => {
  const identifier = getIdentifier(state);

  return identifier.type === IDENTIFIER_TYPES.WIDGET_ID ? identifier.value : '';
};

export const getAllFilterRoomId = (state) => {
  const identifier = getIdentifier(state);

  return identifier.type === IDENTIFIER_TYPES.ROOM_ID ? identifier.value : '';
};

export const getAllFilterPlatformTicketId = (state) => {
  const identifier = getIdentifier(state);

  return identifier.type === IDENTIFIER_TYPES.PLATFORM_TICKET_ID ? identifier.value : '';
};

export const getAllFilterPartialMatch = (state) => state.allFilters?.partialMatch || false;

export const getHasSearchedAllTab = (state) => {
  return state.allFilters?.hasSearched || false;
};

export const getPaginationSkip = (state) => {
  return state.allFilters?.skip || 0;
};

export const getAllFilterParams = (state) => {
  const filters = state.allFilters || {};
  const ticketStatus = filters.ticketStatus || state.selectedTicketStatus;
  const businessIdList = getSelectedBusinessIdList(state);
  const brandOptions = getSelectedBusinessBrandOptionsList(state);
  const excludeBusinesses = excludeSelectedBusinessInFilter(state);
  const simplrTicketNumber = getAllFilterSimplrTicketNumber(state);
  const widgetId = getAllFilterWidgetId(state);
  const roomId = getAllFilterRoomId(state);
  const platformTicketId = getAllFilterPlatformTicketId(state);
  const partialMatch = getAllFilterPartialMatch(state);
  const dateRange = getDateRange(state);
  const skip = getPaginationSkip(state);
  const pagination = getPaginationForAllTab(state);
  const sortOrder = filters.sortOrder || null;

  return {
    ticketStatus,
    businessIdList,
    brandOptions,
    excludeBusinesses,
    simplrTicketNumber,
    widgetId,
    roomId,
    platformTicketId,
    partialMatch,
    dateRange,
    skip,
    pagination,
    sortOrder,
  };
};

export const isTagOnForwardActive = (state) => {
  const tabId = extractSelectedInboxTab(state);

  return state.ui.buttons.tagOnForwardActive && tabId === 'all';
};

export const getNumTicketsForAllTab = (state) => {
  const ticketsInSearch = state.tickets.allTickets.allTickets.length;
  if (ticketsInSearch === 0) {
    return 0;
  }
  const skip = getPaginationSkip(state);

  return ticketsInSearch + skip;
};

export const getPaginationForAllTab = (state) => {
  return (
    state.allFilters?.pagination || {
      hasNextPage: false,
      hasPreviousPage: false,
      nextPageCursor: null,
      previousPageCursor: null,
    }
  );
};

export const getNewPaginationInfoForAllTab = (state) => {
  return {
    hasNextPage: state.tickets.allTickets.hasNextPage,
    hasPreviousPage: state.tickets.allTickets.hasPreviousPage,
    nextPageCursor: state.tickets.allTickets.nextPageCursor,
    previousPageCursor: state.tickets.allTickets.previousPageCursor,
  };
};

export const getAnalytics = (state) => {
  return state.ui.analyticsTracker || {};
};

export const getAnayticsForOutboundMessage = (state) => {
  // TODO: Add grammarly check
  const ticketId = extractSelectedTicketIdFromState(state);
  let analytics = getAnalytics(state);
  let out = {};
  if (analytics[ticketId]) {
    out = Object.assign(out, analytics[ticketId]);
  }
  // add other parameters
  out.ticketSelectionSession = getAnalyticsSessionOfSelectedTicket(state);
  out.abTestingConfig = getAbTestingConfig(state);

  return out;
};

export const checkAnalyticsSessionMatchesSelectedTicket = (state) => {
  const ticketId = extractNonHistoricalTicketIdFromState(state);
  const { ticketSelection } = getAnalytics(state);

  return ticketSelection && ticketSelection.ticketId === ticketId;
};

export const getAnalyticsSessionOfSelectedTicket = (state) => {
  // if by some reason the selected ticket doesn't match the anaytics tracker, return null
  if (!checkAnalyticsSessionMatchesSelectedTicket(state)) {
    return null;
  }

  return getAnalytics(state)['ticketSelection']['session'];
};

export const getSimplrAccountIdOfSelectedTicket = (state) => {
  const ticket = extractSelectedTicketFromState(state);

  return get(ticket, 'simplrAccountPtrId');
};

export const getselectedConversationId = (state) => {
  const { selectedConversationId } = state.conversation;

  return selectedConversationId;
};

export const getBusinessBlacklistWordsInfoOfSelectedTicket = (state) => {
  const simplrAccountId = getSimplrAccountIdOfSelectedTicket(state);

  return getBusinessBlacklistWordsInfoBySimplrAccountId(state, simplrAccountId);
};

export const getBusinessBlacklistRegexesInfoOfSelectedTicket = (state) => {
  const simplrAccountId = getSimplrAccountIdOfSelectedTicket(state);

  return getBusinessBlacklistRegexesInfoBySimplrAccountId(state, simplrAccountId);
};

export const getBusinessBlacklistRegexesInfoBySimplrAccountId = (state, simplrAccountPtrId) => {
  const business = getBusinessById(state, simplrAccountPtrId);
  if (business) {
    return business.blacklistRegexes;
  }

  return undefined;
};

export const getBusinessBlacklistWordsInfoBySimplrAccountId = (state, simplrAccountPtrId) => {
  const business = getBusinessById(state, simplrAccountPtrId);
  if (business) {
    return business.blacklistWords;
  }

  return undefined;
};

export const getSelectedInboxTab = (state) => state.ui.messages.selectedInboxTab;
const getAssignedTickets = (state) => state.tickets.assignedTickets;
const getSelectedAssignedTickets = (state) => state.selectedTicketId;

export const getSelectedAssignedTicketData = createSelector(
  [getSelectedInboxTab, getAssignedTickets, getSelectedAssignedTickets, extractSelectedHistoricalTicketFromState],
  (selectedInboxTab, assignedTickets, selectedTicketId, historicalTicket) => {
    let selectedTicket = {
      ticket: {},
      selectedInboxTab,
    };
    if (historicalTicket) {
      Object.assign(selectedTicket.ticket, historicalTicket);
    } else if (selectedTicketId === '') {
      if (assignedTickets.allTickets.length > 0) {
        let ticketId = assignedTickets.allTickets[0];
        Object.assign(selectedTicket.ticket, assignedTickets[ticketId]);
      }
    } else {
      if (assignedTickets[selectedTicketId] !== undefined) {
        Object.assign(selectedTicket.ticket, assignedTickets[selectedTicketId]);
      }
    }

    return selectedTicket;
  }
);

export const getLastPageUrl = createSelector([getSelectedAssignedTicketData], (selectedTicket) =>
  get(selectedTicket, 'ticket.extraTicketInfo[0].lastPageUrl', null)
);

const getAllMessageIds = (state) => state.conversation.allMessages;
const getConversation = (state) => state.conversation;

export const getConversationMoreTicketInfo = createSelector(
  [extractSelectedTicketFromState],
  (selectedTicket) => selectedTicket.moreTicketInfo
);

export const getAllMessages = createSelector([getAllMessageIds, getConversation], (allMessageIds, conversation) => {
  return allMessageIds.map((id) => conversation[id]);
});

export const getMediaUrlsInConversation = createSelector([getAllMessages], (allMessages) => {
  return allMessages.reduce((images, message) => {
    if (message.direction === INBOUND) {
      return [...images, ...message.mediaUrl];
    }

    return images;
  }, []);
});

export const getIdsToExperts = (state) => state.experts.idsToExperts;

export const getExperts = createSelector(getIdsToExperts, (idsToExperts) => values(idsToExperts));

export const getIdsToCouponCodeTypes = (state) => state.couponCodes.idsToCouponCodeTypes;

export const getDecisionTreeCouponCodeTypes = (state) => {
  const idsToCouponCodeTypes = getIdsToCouponCodeTypes(state);
  if (idsToCouponCodeTypes) {
    return Object.values(idsToCouponCodeTypes).filter((type) => type.isDecisionTreeCoupon);
  }

  return [];
};

export const getSearchId = (state) => {
  return state.tools.ticketSearch.searchId;
};

export const extractTestTicketStatusFromState = (state) => {
  const businessDetailsForTicket = extractSelectedTicketBusinessDetailsFromState(state);

  return businessDetailsForTicket && businessDetailsForTicket.accountType === 'TEST_STORE' && !isSpecialist(state);
};

export const isChatAlertDisabled = (state) => {
  return state.ui.sounds.chatAlertDisabled;
};

export const isNonChatAlertDisabled = (state) => {
  return state.ui.sounds.nonChatAlertDisabled;
};

export const getExpertsForForwardingDropdownList = (state) =>
  state.forwardToExpert.pagination.idList.map((id) => state.forwardToExpert.all[id]);

export const getSelectedExpertForForForwardingDropdown = (state) => state.forwardToExpert.selectedExpert;

export const expertsForForwardingDropdownNextPageExist = (state) => !state.forwardToExpert.pagination.full;

export const hasSelectedExpertForForwarding = (state) => {
  return getSelectedExpertForForForwardingDropdown(state).id !== null;
};

// return true if tagging is available though not necessarily required
export const isSimplrTaggingAvailableForSelectedTicket = (state) => {
  const businessDetailsForTicket = extractSelectedTicketBusinessDetailsFromState(state);
  const selectedTicket = extractSelectedTicketFromState(state);
  // if no ticket selected, no tagging
  // if live chat, no tagging
  if (!selectedTicket || isLiveChat(selectedTicket.communicationType)) {
    return false;
  }

  return businessDetailsForTicket?.simplrCategories?.categories?.length > 0;
};

// is simplr tagging active for the selected ticket
export const isSimplrCategoryTaggingActiveForSelectedTicket = (state) => {
  const taggingAvailable = isSimplrTaggingAvailableForSelectedTicket(state);
  if (!taggingAvailable) {
    return false;
  }
  let selectedTicket = extractSelectedTicketFromState(state);
  if (!selectedTicket) {
    return false;
  }
  const { tagHistory = [] } = selectedTicket;

  // if ticket last tagged by specialists, no need to re-tag
  return tagHistory.length > 0 && last(tagHistory)['isAdmin'] ? false : true;
};

// is simplr tagging required for the selected ticket and based off whether we are using tag on forward
export const isSmbTaggingRequiredForSelectedTicket = (state) => {
  if (isSpecialist(state)) {
    // check if we are forwarding to an expert
    const forwardingFromAllTab = isTagOnForwardActive(state);
    // no smb tagging on forwarding
    if (forwardingFromAllTab) {
      return false;
    }
  }
  if (isSelectedTicketZendeskChat(state) || isSelectedTicketZendeskSMS(state)) {
    return true;
  }

  // no need to re-tag if already set, or if live chat, no tagging
  if (selectedTicketHasSmbTags(state) || isSelectedTicketLiveChat(state)) {
    return false;
  }

  // otherwise check if we have any smb categories
  const tagData = getPartnerTagsArray(state);

  return !isEmpty(tagData?.[0]?.tags);
};

export const isSelectedTicketZendeskChat = (state) => {
  const ticket = extractSelectedTicketFromState(state);

  return ticket && ticket.ticketType === conversationTypes.ZENDESK_CHAT;
};

export const isSelectedTicketZendeskSMS = (state) => {
  const ticket = extractSelectedTicketFromState(state);

  return (
    ticket &&
    ticket.ticketType === conversationTypes.ZENDESK &&
    ticket.communicationType === conversationTypes.SMS_MESSAGE
  );
};

export const extractSimplrTagForSelectedTicket = (state) => {
  let selectedTicket = extractSelectedTicketFromState(state);

  return (selectedTicket && selectedTicket.simplrCategories && selectedTicket.simplrCategories[0]) || '';
};

export const extractCustomerNameForSelectedTicket = (state) => {
  let selectedTicket = extractSelectedTicketFromState(state);
  const firstName = (selectedTicket && selectedTicket.customerFirstName) || null;

  return firstName ? firstName : null;
};

export const extractMlDataOrdersIds = (state) => state.mlData.ordersIds;

export const extractMlOrdersIdsDataFromState = _createSelector(
  [extractSelectedTicketFromState, extractMlDataOrdersIds],
  (selectedTicket, mlDataOrdersIds) => {
    const ticketId = selectedTicket && selectedTicket.ticketId;

    return ticketId ? get(mlDataOrdersIds, ticketId, {}) : {};
  }
);

export const getMLMentionedOrdersFromState = _createSelector([extractMlOrdersIdsDataFromState], (mlCustomerData) =>
  get(mlCustomerData, 'orderInfoInTickets', [])
);

export const extractMLMentionedOrdersFromState = _createSelector(
  [getMLMentionedOrdersFromState],
  (mlCustomerData) => mlCustomerData
);

export const extractRecentMLOrdersFromState = _createSelector([extractMlOrdersIdsDataFromState], (mlCustomerData) =>
  get(mlCustomerData, 'allOrderInfo', [])
);

export const extractRecentSubscriptionsFromState = _createSelector(
  [extractMlOrdersIdsDataFromState],
  (mlCustomerData) => get(mlCustomerData, 'allSubscriptionInfo', [])
);

export const extractListOfMentionedOrderIdsFromState = _createSelector(
  [extractMlOrdersIdsDataFromState],
  (mlCustomerData) => get(mlCustomerData, 'orderIdsMentionedInTickets', [])
);

export const getIdOfSelectedMacro = (state) => {
  return get(state, 'tools.macroWizard.selectedMacro.objectId', null);
};

export const getBusinessAlerts = (state) => {
  return state.tools.businessAlerts;
};

export const getViewedBlockingAlertIds = (state) => {
  return state.tools.alertsManager.viewedBlockingAlertIds;
};

export const getAllActivePartnerAlerts = (state) => {
  const allAlerts = getBusinessAlerts(state) || {};

  const activeTicketBrand = getSelectedTicketBrand(state);

  if (activeTicketBrand) {
    return Object.values(allAlerts)
      .filter(
        (alert) =>
          alert.status === 'ACTIVE' &&
          (moment(alert.startDate).isBefore(moment.now()) || !alert.startDate) &&
          (isEmpty(alert.brands) || alert.brands.includes(activeTicketBrand))
      )
      .sort((a, b) => b.updatedAt - a.updatedAt);
  }

  return Object.values(allAlerts)
    .filter(
      (alert) => alert.status === 'ACTIVE' && (moment(alert.startDate).isBefore(moment.now()) || !alert.startDate)
    )
    .sort((a, b) => b.updatedAt - a.updatedAt);
};

// returns alerts within scope (category, tag, macro)
export const getAlertsForLevel = (state) => {
  const searchType = getSearchTypeOfSelectedAction(state);
  const allActiveAlerts = getAllActivePartnerAlerts(state);
  const { tagPath } = extractMacroWizardObject(state);
  const searchAllResult = getSelectedSearchResult(state);
  let selectedMacroId;
  const filteredAlerts = allActiveAlerts.filter((alert) => {
    // pull scoping data from alert
    const alertMacroIds = get(alert, 'scope.MACRO_LEVEL', []);
    const alertCategories = get(alert, 'scope.CATEGORY_LEVEL', []);
    const alertTags = get(alert, 'scope.TAG_LEVEL', []);
    // shared information
    const isGeneralAlert = !alertCategories.length && !alertTags.length && !alertMacroIds.length;
    if (searchType === MACRO_WIZARD) {
      selectedMacroId = getIdOfSelectedMacro(state);
      // find matches
      const categoryMatch = alertCategories.some((category) => tagPath.includes(category));
      const tagMatch = alertTags.some((tag) => tag === tagPath);
      const macroMatch = alertMacroIds.some((id) => id === selectedMacroId);
      // filter
      const hasLevelMatch = categoryMatch || tagMatch || macroMatch;

      return isGeneralAlert || hasLevelMatch;
    } else if (searchType === SEARCH_ALL) {
      if (!isEmpty(searchAllResult)) {
        // example algoliaId: PARSE_MACRO_QM5aGDjy3F
        const algoliaId = searchAllResult.objectID;
        if (algoliaId) {
          const splitId = algoliaId.split('_');
          selectedMacroId = splitId[splitId.length - 1];
        }
        const macroTags = searchAllResult.tags || [];
        const macroCategories = macroTags.map((tag) => tag.split(' -- ')[0]);
        // find matches
        const categoryMatch = alertCategories.some((category) => macroCategories.includes(category));
        const tagMatch = alertTags.some((tag) => macroTags.includes(tag));
        const macroMatch = alertMacroIds.some((id) => !!selectedMacroId && id === selectedMacroId);
        // filter on matches
        const hasLevelMatch = tagMatch || macroMatch || categoryMatch;

        return isGeneralAlert || hasLevelMatch;
      }

      return isGeneralAlert;
    }

    return false;
  });

  return filteredAlerts;
};

// returns blocking alert one at a time
export const getCurrentBlockingAlert = (state) => {
  const searchType = getSearchTypeOfSelectedAction(state);
  const allActiveAlerts = getAllActivePartnerAlerts(state);
  const { tagPath } = extractMacroWizardObject(state);
  const searchAllResult = getSelectedSearchResult(state);
  const viewedBlockingAlertIds = getViewedBlockingAlertIds(state);
  const expertOnTopLevel = !tagPath.length;
  let selectedMacroId;
  const blockingAlerts = allActiveAlerts.filter((alert) => {
    // pull scoping data from alert
    const alertMacroIds = get(alert, 'scope.MACRO_LEVEL', []);
    const alertCategories = get(alert, 'scope.CATEGORY_LEVEL', []);
    const alertTags = get(alert, 'scope.TAG_LEVEL', []);
    // shared information
    const isUnread = alert.readStatus !== READ;
    const isGeneralAlert = !alertCategories.length && !alertTags.length && !alertMacroIds.length;
    const blockingAlertHasNotBeenViewed = !viewedBlockingAlertIds.includes(alert.id);
    const shouldBlock =
      (alert.blockType === ALWAYS_BLOCK && blockingAlertHasNotBeenViewed) ||
      ([BLOCK_DAILY, BLOCK_WEEKLY, BLOCK_ONCE].includes(alert.blockType) && isUnread);

    if (searchType === MACRO_WIZARD) {
      selectedMacroId = getIdOfSelectedMacro(state);
      // find matches
      const categoryMatch = alertCategories.some(
        (category) => tagPath && tagPath.split(' -- ').length === 1 && tagPath.split(' -- ')[0] === category
      );
      const tagMatch = alertTags.some((tag) => tag === tagPath);
      const macroMatch = alertMacroIds.some((id) => id === selectedMacroId);
      // filter on matches
      const hasLevelMatch = categoryMatch || tagMatch || macroMatch;

      return shouldBlock && ((isGeneralAlert && expertOnTopLevel) || hasLevelMatch);
    } else if (searchType === SEARCH_ALL) {
      if (!isEmpty(searchAllResult)) {
        // example algoliaId: PARSE_MACRO_QM5aGDjy3F
        const algoliaId = searchAllResult.objectID;
        if (algoliaId) {
          const splitId = algoliaId.split('_');
          selectedMacroId = splitId[splitId.length - 1];
        }
        const macroTags = searchAllResult.tags || [];
        const macroCategories = macroTags.map((tag) => tag.split(' -- ')[0]);
        // find matches
        const categoryMatch = alertCategories.some((category) => macroCategories.includes(category));
        const tagMatch = alertTags.some((tag) => macroTags.includes(tag));
        const macroMatch = alertMacroIds.some((id) => !!selectedMacroId && id === selectedMacroId);
        // filter on matches
        const hasLevelMatch = tagMatch || macroMatch || categoryMatch;

        return shouldBlock && hasLevelMatch;
      }

      return false;
    }

    return false;
  });
  // organize filtered alerts by type
  const blockingAlertsByType = {};
  blockingAlerts.forEach((alert) => {
    // for backwards compatibility
    if (!alert.type) {
      if (blockingAlertsByType[GUIDE]) {
        blockingAlertsByType[GUIDE].push(alert);
      } else {
        blockingAlertsByType[GUIDE] = [alert];
      }
    } else if (blockingAlertsByType[alert.type]) {
      blockingAlertsByType[alert.type].push(alert);
    } else {
      blockingAlertsByType[alert.type] = [alert];
    }
  });

  let currentBlockingAlert = null;
  alertsTypesList.forEach((type) => {
    if (blockingAlertsByType[type] && !currentBlockingAlert) {
      currentBlockingAlert = blockingAlertsByType[type][0];
    }
  });

  return currentBlockingAlert;
};

export const getAllEscalationAlertsForLevel = (state) => {
  const allLevelAlerts = getAlertsForLevel(state);

  return allLevelAlerts.filter((alert) => alert.type === ESCALATION || alert.type === ESCALATION_PARTNER);
};

export const getAllActiveAlertsByType = (state) => {
  const allActiveAlerts = getAllActivePartnerAlerts(state);
  const result = {};
  allActiveAlerts.forEach((alert) => {
    // for backwards compatibility
    if (!alert.type) {
      if (result[GUIDE]) {
        result[GUIDE].push(alert);
      } else {
        result[GUIDE] = [alert];
      }
    } else if (result[alert.type]) {
      result[alert.type].push(alert);
    } else if (alert.type === ESCALATION_PARTNER && result[ESCALATION]) {
      result[ESCALATION].push(alert);
    } else {
      if (alert.type === ESCALATION_PARTNER && !result[ESCALATION]) {
        result[ESCALATION] = [alert];
      }
      result[alert.type] = [alert];
    }
  });

  return result;
};

export const doesBusinessHaveAlerts = (state) => {
  return Object.keys(getBusinessAlerts(state)).length > 0;
};

export const getNumberOfUnreadBusinessAlerts = (state) => {
  const businessAlerts = Object.values(getBusinessAlerts(state));
  let counter = 0;
  businessAlerts.forEach((alert) => {
    if (
      alert.readStatus !== 'READ' &&
      alert.status === 'ACTIVE' &&
      (moment(alert.startDate).isBefore(moment.now()) || !alert.startDate)
    ) {
      counter++;
    }
  });

  return counter;
};

export const getBusinessesListPagination = (state) => state.businesses.pagination;

export const getAllBusinessesList = createSelector(
  [getAllBusinessesObject, getBusinessesListPagination],
  (allBusinesses, pagination) => pagination.idList.map((id) => allBusinesses[id])
);

export const getBusinessesNamesAndIds = createSelector([getAllBusinessesList], (allBusinesses) =>
  allBusinesses.map((item) => ({
    value: item.id,
    label: item.smbName,
  }))
);

export const getBusinessById = _createSelector(
  [getAllBusinessesObject, (_, simplrAccountId) => simplrAccountId],
  (allBusinesses, simplrAccountId) => {
    return allBusinesses?.[simplrAccountId];
  }
);

export const getBusinessNameAndIdById = _createSelector([getBusinessById], (businessDetail) => {
  if (!businessDetail) {
    return null;
  }

  return {
    value: businessDetail.id,
    label: businessDetail.smbName,
  };
});

export const businessDetailsNextPageExist = (state) => !state.businesses.pagination.full;

export const getAbTestingConfig = (state) => {
  return state.abTesting;
};

const extractMacroWizardObject = (state) => state.tools.macroWizard || {};

export const getMacroWizardTagPathArray = _createSelector([extractMacroWizardObject], ({ tagPath }) =>
  splitTagPath(tagPath)
);

export const extractMacroWizardTagPathArray = _createSelector([getMacroWizardTagPathArray], (tagPath) => tagPath);

export const extractMacroWizardLoading = (state) => {
  const { loading } = extractMacroWizardObject(state);

  return loading;
};

export const extractCurrentMacroWizardDepth = (state) => {
  const splittagPath = extractMacroWizardTagPathArray(state);
  const currDepth = splittagPath.length;

  return currDepth;
};

export const extractMacroListByPath = createSelector(
  [extractMacroWizardObject],
  (macroWizardObj) => macroWizardObj.macrosByTagPath
);

export const extractDecisionFlowsData = createSelector(
  [extractMacroWizardObject],
  (macroWizardObj) => macroWizardObj.decisionFlowsData
);

export const extractSelectedWizardMacro = createSelector(
  [extractMacroWizardObject],
  (macroWizardObj) => macroWizardObj.selectedMacro
);

export const extractCurrentMacroWizardPath = createSelector(
  [extractMacroWizardObject],
  (macroWizardObj) => macroWizardObj.tagPath
);

export const extractMacroSnippets = createSelector(
  [extractMacroWizardObject],
  (macroWizardObj) => macroWizardObj.snippetTextList || []
);

// Note: this excludes escalation macros (see getEscalationMacroList)
export const getOrderedMacroList = createSelector(
  [extractMacroListByPath, extractCurrentMacroWizardPath, getBrandOfSelectedTicket],
  (macroListByPath, tagPath, brand) => {
    const macroList = (macroListByPath[tagPath] || []).filter((macro) => !isExpiredMacro(macro));

    const newItems = macroList
      .filter(checkIfMacroIsNew)
      .filter((macro) => macro.macroType !== macroTypes.JIT_ESCALATION); // filter out escalation macros (will be put on the top)

    const oldItems = macroList
      .filter((macro) => !checkIfMacroIsNew(macro))
      .filter((macro) => macro.macroType !== macroTypes.JIT_ESCALATION) // filter out escalation macros (will be put on the top)
      .sort((a, b) => sortMacrosByUsage(a, b));

    const allItems = [...newItems, ...oldItems];

    if (brand) {
      return allItems.filter((macro) => macro.brand === brand || macro.brand === 'undefined' || !macro.brand);
    }

    return allItems;
  }
);

// legacy - escalation macros should not be used
export const getEscalationMacroList = createSelector(
  [extractMacroListByPath, extractCurrentMacroWizardPath, getBrandOfSelectedTicket],
  (macroListByPath, tagPath, brand) => {
    const macroList = macroListByPath[tagPath] || [];
    const escalationMacros = macroList.filter((macro) => macro.macroType === macroTypes.JIT_ESCALATION);
    const allItems = [...escalationMacros];
    if (brand) {
      return allItems.filter((macro) => macro.brand === brand || macro.brand === 'undefined' || !macro.brand);
    }

    return allItems;
  }
);

export const extractNoMatchPageActivated = createSelector(
  [extractMacroWizardObject],
  (macroWizardObj) => macroWizardObj.noMatchPageActivated
);

export const extractCurrentDecisionTreePath = createSelector(
  [extractMacroWizardObject],
  (macroWizardObj) => macroWizardObj.decisionTreePath
);

export const extractCurrentDecisionTreePathArray = createSelector(
  [extractCurrentDecisionTreePath],
  (decisionTreePath) => {
    return splitTagPath(decisionTreePath);
  }
);

// Do not use this function, use extractDecisionTreeOfSelectedTicket
export const _extractSelectedPartnerTrees = (state) => {
  return get(state, 'tools.decisionTree.selectedPartnerTrees', {});
};

export const extractSimplrAccountIdOfLoadedTrees = (state) => {
  return get(state, 'tools.decisionTree.selectedPartnerTrees.simplrAccountId');
};

export const extractDecisionTreeOfSelectedTicket = createSelector(
  [getSimplrAccountIdOfSelectedTicket, _extractSelectedPartnerTrees, extractSimplrAccountIdOfLoadedTrees],
  (simplrAccountIdOfSelectedTicket, partnerTrees, simplrAccountIdOfLoadedTrees) => {
    // Protect against returning stale data for different partner
    if (!!simplrAccountIdOfSelectedTicket && simplrAccountIdOfLoadedTrees === simplrAccountIdOfSelectedTicket) {
      return get(partnerTrees, 'trees', {});
    }

    return {};
  }
);

export const extractRootDecisionTreeAtPath = createSelector(
  [extractDecisionTreeOfSelectedTicket, extractCurrentMacroWizardPath],
  (decisionTree, macroWizardPath) => {
    return decisionTree ? decisionTree[macroWizardPath] : undefined;
  }
);

// Selects from the node listed directly on the tree
export const extractNodeFromSpecifiedTreeLocation = (currTreeLocation) => {
  return get(currTreeLocation, 'node');
};

export const extractDecisionTreeAtPath = (state) => {
  // start at the root of the tree
  let currTree = extractRootDecisionTreeAtPath(state);
  if (!currTree) {
    return null;
  }

  // traverse down tree based off decision tree path
  const decisionTreePath = extractCurrentDecisionTreePath(state);

  return getTreeAtPath(currTree, decisionTreePath);
};

export const extractMacroWizardBreadcrumbPath = (state) => {
  const macroWizardObj = extractMacroWizardObject(state);
  let currTree = extractRootDecisionTreeAtPath(state);
  const currNode = getDecisionNodeAtPath(state);
  const { tagPath, decisionTreePath } = macroWizardObj;
  let fullSplitPath = splitTagPath(tagPath);
  const splitDecisionPath = splitTagPath(decisionTreePath);

  if (!currTree) {
    return fullSplitPath;
  }
  // traverse our decision tree path
  // if we have breadcrumb=false items they get shown on a single page with a single breadcrumb
  for (let i = 0; i < splitDecisionPath.length; i++) {
    const choice = splitDecisionPath[i];
    // get the full node based off the id or directly off the tree structure
    const decisionNode = extractNodeFromSpecifiedTreeLocation(currTree);

    // if we have breadcrumb=false elements, they don't get added to the breadcrumb
    // also skip the 0th item
    if (i > 0 && decisionNode?.breadcrumb) {
      const breadcrumbTitle = getBreadcrumbTitleOfNode(decisionNode) || choice;
      fullSplitPath.push(breadcrumbTitle);
    }

    // find next node
    currTree = currTree.children[choice];
  }
  // add the current node in the path if title available
  const breadcrumbTitle = currNode && getBreadcrumbTitleOfNode(currNode);
  if (breadcrumbTitle) {
    fullSplitPath.push(breadcrumbTitle);
  }

  return fullSplitPath;
};

// nodes are stored in three ways - by id, by nameToSpecialists (unique key),
// or by listing the node directly on the tree
export function getDecisionNodeAtPath(state) {
  const subTree = extractDecisionTreeAtPath(state);
  if (!subTree) {
    return null;
  }

  return subTree?.node;
}

export const getDecisionNodeChildrenPaths = (state) => {
  const subTree = extractDecisionTreeAtPath(state);
  if (!subTree) {
    return [];
  }

  return subTree?.node ? Object.keys(subTree.children || {}) : [];
};

export const getSelectedReturnLabelLink = (state) => {
  return state.tools.ordersSearch.selectedReturnLabelLink;
};

export const getSelectedCouponCodeType = (state) => {
  return state.tools.ordersSearch.selectedCouponCodeType;
};

export const getCancelAndRefundItems = (state) => {
  return get(state, 'tools.ordersSearch.cancelAndRefundItems', []);
};

export const getRefundOnlyItems = (state) => {
  return get(state, 'tools.ordersSearch.refundOnlyItems', []);
};
export const getCancelOnlyItems = (state) => {
  return get(state, 'tools.ordersSearch.cancelOnlyItems', []);
};

export const getDiscountOnlyItems = (state) => {
  return get(state, 'tools.ordersSearch.discountOnlyItems', []);
};

export const getReturnShipmentTrackingNumber = (state) => {
  return state.tools.ordersSearch.returnShipmentTrackingNumber;
};

export const getReturnShipmentCarrier = (state) => {
  return state.tools.ordersSearch.returnShipmentCarrier;
};

export function getCurrentDecisionNodeContent(state) {
  let content = {
    title: null,
    subText: null,
  };

  const decisionNode = getDecisionNodeAtPath(state);
  if (!decisionNode) {
    return null;
  }
  content.title = decisionNode.title;
  content.subText = decisionNode.subText;

  return content;
}

export const getSubscriptionSearchParams = (state) => {
  return state.tools.subscriptionSearch.searchParams;
};

export const getChildrenOfCurrentDecisionNode = (state) => {
  const subTree = extractDecisionTreeAtPath(state);
  if (!subTree || !subTree.children) {
    return null;
  }
  const children = {};
  // using new node system
  for (let choice in subTree.children) {
    children[choice] = subTree.children[choice] && subTree.children[choice].node;
  }

  return children;
};

export function getActionsOfAncestorsOfDecisionTree(state) {
  const splitDecisionPath = extractCurrentDecisionTreePathArray(state);
  let currTree = extractRootDecisionTreeAtPath(state);
  const ancestorActions = [];

  if (!currTree) {
    return ancestorActions;
  }

  for (let choice of splitDecisionPath) {
    const currNode = extractNodeFromSpecifiedTreeLocation(currTree);
    ancestorActions.push(currNode.action);
    currTree = currTree.children[choice];
  }
  // add final choice
  if (currTree) {
    const currNode = extractNodeFromSpecifiedTreeLocation(currTree);
    if (currNode) {
      ancestorActions.push(currNode.action);
    }
  }

  return ancestorActions;
}

export const getSimplrTaggingActive = (state) => state.tools.macroWizard.taggingActive;

export const getPossibleNextMacroWizardEdge = createSelector(
  [
    extractMacroListByPath,
    extractCurrentMacroWizardPath,
    extractCurrentMacroWizardDepth,
    extractDecisionTreeOfSelectedTicket,
    getBrandOfSelectedTicket,
    extractSimplrTagsForSelectedTicket,
    extractDecisionFlowsData,
  ],
  (macroListByPath, tagPath, currDepth, decisionTree, brand, simplrTags, decisionFlowsData) => {
    // copy data to avoid mutation
    const macroListByPathCopy = cloneDeep(macroListByPath);
    const decisionTreeCopy = decisionTree ? cloneDeep(decisionTree) : {};
    const { categoryRules, categories, brandCategories } = simplrTags;

    if (!categories || !categoryRules) {
      return [];
    }

    const potentialMacroPaths = Object.keys(macroListByPathCopy);
    const outOfScopePaths = potentialMacroPaths.filter(
      (path) => categoryRules[path] === false && macroListByPathCopy[path].length === 0
    );

    // NOTE: macroListByPathCopy includes both in-scope and out-of-scope options
    // Operations does not want us to remove in-scope options without content

    // filter macros by brand (decision trees filtered on server)
    if (brand) {
      // keep if macros have brand that matches OR no brand
      potentialMacroPaths.forEach((path) => {
        let shouldKeep = macroListByPathCopy[path].some(
          (macro) => macro.brand === brand || macro.brand === 'undefined' || !macro.brand
        );
        // keep out of scope options - they are univeral
        shouldKeep = shouldKeep || outOfScopePaths.includes(path);
        if (!shouldKeep) {
          delete macroListByPathCopy[path];
        }
      });
    }

    // filter out tags that do not apply for the current brand
    let allValidTagsForPartner;
    if (brand && brandCategories) {
      const brandTags = brandCategories[brand] || [];
      const commonTags = brandCategories['common'] || [];
      const tagValidForCurrentBrand = uniq([...brandTags, ...commonTags]);
      // maintain the ordering in set in the admin portal
      allValidTagsForPartner = categories.filter((tag) => {
        return tagValidForCurrentBrand.includes(tag);
      });
    } else {
      // use all tags stored for partner
      allValidTagsForPartner = [...categories];
    }

    // Merge decisionTreeCopy and decisionFlowsData to ensure tags used by the Logic Flow Manager are considered
    const mergedDecisionData = { ...decisionTreeCopy, ...decisionFlowsData };

    // get all paths with content filtered against the brand
    let macroPaths = Object.keys(macroListByPathCopy);
    let decisionTreePaths = Object.keys(mergedDecisionData);

    // filter against all existing tags to maintain ordering
    const allPaths = allValidTagsForPartner.filter((tag) => {
      return macroPaths.includes(tag) || decisionTreePaths.includes(tag);
    });

    const matchingPaths = allPaths.filter((path) => {
      if (!path.startsWith(tagPath)) {
        return false;
      }
      if (
        decisionFlowsData &&
        !decisionFlowsData[path] &&
        !outOfScopePaths.includes(path) &&
        !macroPaths.includes(path)
      ) {
        // temporary solution, until full migration from DTs to logic flows
        return false;
      }

      return path.length > tagPath.length;
    });

    let nextPaths = matchingPaths.map((path) => {
      // trim off extra path
      const splitPath = splitTagPath(path);
      const nextPath = joinTagArray(splitPath.slice(0, currDepth + 1));

      return nextPath;
    });
    nextPaths = uniq(nextPaths);

    return nextPaths;
  }
);

export const extractAllMacroWizardPaths = createSelector(
  [extractMacroListByPath, extractDecisionTreeOfSelectedTicket],
  (macroListByPath, decisionTree) => {
    // first start with the keys which represent the macro tags
    let allPaths = Object.keys(macroListByPath);
    // then pull the keys from the decision tree
    decisionTree = decisionTree || {};
    allPaths = allPaths.concat(Object.keys(decisionTree));

    return uniq(allPaths);
  }
);

export const getIsMacroWizardEmpty = createSelector([extractMacroListByPath], (macroListByPath) => {
  return isEmpty(macroListByPath);
});

export const isMacroWizardActiveForBusinessOfSelectedTicket = createSelector(
  [getIsMacroWizardEmpty],
  (isMacroWizardEmpty) => {
    // macro wizard is active as long as the wizard is not empty
    return !isMacroWizardEmpty;
  }
);

export const getPartnerTagsArray = (state) => {
  return state.partnerTags || [];
};

export const hasMessageBeenRead = (state) => {
  const selectedTicketId = extractSelectedTicketIdFromState(state);

  return state.ui.scrollTracking[selectedTicketId];
};

export const getTicketRemovalInProcess = (state) => {
  const selectedTicketId = extractSelectedTicketIdFromState(state);

  return state.ui.display.ticketRemovalInProcess.includes(selectedTicketId);
};

export const isTicketBeingViewed = (state, ticketId) => {
  const selectedTab = getSelectedInboxTab(state);
  const selectedTicket = extractTicketFromStateById(state, ticketId);

  if (!selectedTicket) {
    return false;
  }

  if (selectedTab === 'assigned' && isLiveChat(selectedTicket.communicationType)) {
    return isSpecialist(state) && state.selectedTicketId === ticketId;
  }

  if (selectedTab === 'live_chat') {
    return isLiveChat(selectedTicket.communicationType) && state.selectedTicketId === ticketId;
  }

  return state.selectedTicketId === ticketId;
};

export const selectedTicketHasSmbTags = (state) => {
  const selectedTicket = extractSelectedTicketFromState(state);

  // currently just seeing if the value has been set at all, not checking that the tags match what is necessary
  return selectedTicket && selectedTicket.platformTags && selectedTicket.platformTags.length > 0;
};

export const getQueueNamesForTicket = (state, ticketId) => {
  const queueNames = [];
  if (state.tickets.allTickets[ticketId]) {
    queueNames.push('all');
  } else if (state.tickets.assignedTickets[ticketId]) {
    queueNames.push('assigned');
  } else if (state.tickets.nominatedTickets[ticketId]) {
    queueNames.push('nominated');
  }

  return queueNames;
};

export const getBlockingSpinnerActiveStatus = (state) => {
  return (
    (state.ui.loading.isLoading &&
      ['CopyTextContainer', 'ChangeAddressContainer', 'DecisionTreeContainer', 'ConversationRating'].includes(
        state.ui.loading.component
      )) ||
    isLoadingUnique(state, 'CopyTextContainer')
  );
};

// this is made to work with setLoadingUnique, to make sure components don't overwrite loading states
export const isLoadingUnique = (state, component) => !!state.ui.loading.unique?.[component];

export const getChatAlertActiveId = (state) => {
  return state.ui.chatAlertActive.activeId;
};

export const getDiscountPeriods = (state) => state.tools.ordersSearch.discountPeriods;

export const getItemAndDiscountPeriodMatches = (state) => state.tools.ordersSearch.itemAndDiscountPeriodMatches;

export const getSelectedDiscountPeriod = (state) => state.tools.ordersSearch.selectedDiscountPeriod;

export const getSelectedDiscountPeriodCouponCode = (state) =>
  get(state, 'tools.ordersSearch.selectedDiscountPeriod.couponCode', undefined);

export const getDiscountDescription = (state) => state.tools.ordersSearch.selectedDiscountPeriod.discountDescription;

export const getEscalationMessage = (state) => state.ui.messages.escalationMessage;

export const getOutboundEscalationObject = (state) => state.ui.messages.outboundEscalationObject;

export const shouldDisplayChatMacroRecommendations = (state) => {
  return state.ui.messages.chatMacroRecommendationsEnabled && chatMacroRecommendationsAvailable(state);
};

// Get list of ids for tickets where the expert has seen the escalation recommendation popup already
export const getEscRecoSeenForTickets = (state) => {
  return state.ui.escalationRecommendation.escRecoSeenForTickets;
};

export const getEscRecoState = (state) => state.ui.escalationRecommendation;

// Check if escalation recommendation pop up was shown for ticket
export const escRecoSeenForTicket = (state) => {
  const ticket = extractSelectedTicketFromState(state);
  const conversationId = ticket && ticket.ticketId;
  const escRecoSeenForTickets = getEscRecoSeenForTickets(state);

  return escRecoSeenForTickets.includes(conversationId);
};

export const getActiveEscRecoPopupId = createSelector(
  getEscRecoState,
  (EscRecoState) => EscRecoState.activeEscRecoPopupId
);

export const isBusinessAlertActive = (state, alertId) => {
  const businessAlerts = getBusinessAlerts(state);
  if (!businessAlerts || !alertId) {
    return false;
  }
  const isActive = businessAlerts[alertId] && businessAlerts[alertId]['status'] === 'ACTIVE';

  return isActive;
};

export const escalationRecommendationMessage = (state, ticket) => {
  if (!ticket) {
    return null;
  }
  const conversationId = ticket.ticketId;
  const communicationType = ticket.communicationType;
  // Check if ticket id exists and user is expert
  if (!conversationId || isSpecialist(state) || isSpecialistLite(state)) {
    return null;
  }

  // Check if popup already shown or ticket was checked and found irrelevant
  if (escRecoSeenForTicket(state, ticket)) {
    return null;
  }

  if (isLiveChat(communicationType) || !ticket.escalationRecommendation) {
    return null;
  }

  const escalationRecommendation = ticket.escalationRecommendation;
  if (!escalationRecommendation) {
    return null;
  }
  try {
    const shouldDisplay = escalationRecommendation.toDisplay;
    const escalationRecommended = escalationRecommendation.escalationRecommended;
    const businessAlertId = get(escalationRecommendation, 'escalationRecommended.alertId', null);
    const alertIsActive = isBusinessAlertActive(state, businessAlertId);

    return alertIsActive && shouldDisplay && escalationRecommended ? escalationRecommended : null;
  } catch (error) {
    handleError({ error });
  }
};

export const chatMacroRecommendationsAvailable = (state) => {
  const chatMacroRecommendationsEnabled = extractSelectedTicketBusinessDetailsFieldValueFromState(
    state,
    'showChatMacroRecommendations'
  );
  const selectedTicket = extractSelectedTicketFromState(state);
  if (chatMacroRecommendationsEnabled && selectedTicket && isLiveChat(selectedTicket.communicationType)) {
    return true;
  }

  return false;
};

export const getLockedConversationId = (state) => state.lockedConversationId;

export const getPartnerPlatformHistoricalTicketsFromState = (state) => {
  return get(state.tools, 'ticketSearch.partnerPlatformHistoricalTickets', []);
};

export const isPartnerPlatformTicketsSearchEnabled = createSelector(
  [extractSelectedTicketBusinessDetailsFromState],
  (businessDetail) => businessDetail?.ticketsSearchConfig?.partnerPlatformSearch || false
);

export const extractDebounceAmountForGetPredictionTextFromState = (state) =>
  get(state, 'parseConfig.debounceAmountForGetPredictionText', null);

export const getTagHistoryOfSelectedTicket = (state) => {
  const selectedTicket = extractSelectedTicketFromState(state);

  return get(selectedTicket, 'tagHistory', []);
};

export const getPreviousAppVersionFile = (state) => {
  return state.ui.appVersion.previousAppVersionFile;
};

export const getShowAppVersionRefreshNotification = (state) => {
  return (
    state.ui.appVersion.showAppVersionRefreshNotification &&
    !state.notifications.tabNotificationsQueue.length &&
    !getChatAlertActiveId(state)
  );
};

export const isSelectedTicketReviewTicket = (state) => {
  const selectedTicket = extractSelectedTicketFromState(state);

  if (!selectedTicket) {
    return false;
  }

  if (isAPartnerProtector(state)) {
    const isSelectedHistoricalTicketId = !!extractSelectedHistoricalTicketIdFromState(state);
    const isReviewTicket = selectedTicket.reviewExpertId === state.user.id;

    /** NOTE(apoorvyadav1|SPD-11740|2025-03-04): In the case of a historical ticket, we validate if
     * the current conversation is taken by a partner protector.
     * If it is, then the selected ticket is not considered a review ticket.
     * This enables partner protector to view historical tickets for review tickets. In order for accurately categorizing
     * the review tickets we need to validate if the original ticket for the historical conversation is assigned to the expert.
     */
    const originalSelectedTicket = extractTicketFromStateById(state, state.selectedTicketId);
    const isSelectedTicketAssignedToExpert = originalSelectedTicket?.expertId === state.user.id;

    return isReviewTicket || (isSelectedHistoricalTicketId && !isSelectedTicketAssignedToExpert);
  }

  return false;
};

export const getConversationRatingToolConfig = (state) => {
  const businessDetail = extractSelectedTicketBusinessDetailsFromState(state);

  if (state.user.isAdmin) {
    const defaultConfig = get(state, 'parseConfig.specialistRatingToolConfig', {});
    const partnerSpecificConfig = get(businessDetail, 'specialistPlatformRatingToolConfig.specialistConfig');

    return partnerSpecificConfig || defaultConfig;
  }

  if (isAPartnerProtector(state)) {
    const defaultConfig = get(state, 'parseConfig.partnerProtectorRatingToolConfig', {});
    const partnerSpecificConfig = get(businessDetail, 'specialistPlatformRatingToolConfig.partnerProtectorConfig');

    return partnerSpecificConfig || defaultConfig;
  }

  return null;
};

// returns previous selected ticket id in tab if it still exists
export const getPrevSelectedTicketId = (state, tabName) => {
  const prevSelectedTicketIdInTab = state.previousSelectedTicketId[tabName];
  const ticketsQueue = getTicketQueueFromTab(state, tabName);
  const ticketExisits = !!ticketsQueue[prevSelectedTicketIdInTab];

  return ticketExisits ? prevSelectedTicketIdInTab : null;
};

export const getClipboardTextForSpecificTicketId = (state, selectedTicketId) => {
  return get(state, `ui.messages.clipboardText.${selectedTicketId}`, '');
};

export const extractParseConfigValueFromState = (state, configName, defaultValue = null) => {
  return get(state, `parseConfig.${configName}`, defaultValue);
};

export const extractShowSpecialistTools = (state) => {
  return state.ui.userSettings.showSpecialistTools;
};

export const getRbiCustomerIdFromOrder = (state) => {
  return get(state, 'tools.ordersSearch.rbiCustomerId', null);
};

export const getRbiLoyaltyId = (state) => {
  return get(state, 'tools.ordersSearch.rbiLoyaltyId', null);
};

export const getRbiCustomerIdFromLoyaltyInfo = (state) => {
  return get(state, 'tools.ordersSearch.rbiCouponsInfo.loyaltyInfo.customer.customerId', null);
};

export const getRbiLoyaltyCardId = (state) => {
  return get(state, 'tools.ordersSearch.rbiCouponsInfo.loyaltyInfo.loyaltyCard.cardId', null);
};

export const getLegacyCognitoId = (state) => {
  return get(state, 'tools.ordersSearch.rbiCouponsInfo.loyaltyInfo.customer.thLegacyCognitoId', null);
};

export const getRbiCouponsInfo = (state) => {
  return get(state, 'tools.ordersSearch.rbiCouponsInfo', null);
};

export const getRbiAssignedOffers = (state) => {
  return get(state, 'tools.ordersSearch.rbiAssignedOffers', null);
};

export const getSelectedCheggBookIssuesOrder = (state) => {
  return get(state, 'tools.ordersSearch.chegg.selectedBookIssuesOrder', null);
};

export const getIsExpertDashboardOpened = (state) => {
  return get(state, 'ui.userSettings.expertDashboardOpened', false);
};

export const getFlowDebugModeStatus = (state) => {
  return get(state, 'ui.userSettings.debugModeEnabled', false);
};

export const extractCustomerGroups = (state) => {
  return get(state, 'tools.ordersSearch.customerGroups.groups', []);
};

export const extractSelectedCustomerGroupId = (state) => {
  return get(state, 'tools.ordersSearch.customerGroups.selectedCustomerGroupId');
};

export const extractCommerceWizardRecommendationData = (state) => {
  const ticketId = extractSelectedTicketIdFromState(state);

  return get(state, `mlData.recommendedProducts.${ticketId}`, {});
};

export const extractCommerceWizardPath = (state) => {
  return get(state, 'tools.commerceWizard.path', []);
};

export const extractCommerceWizardSelectedRecommendation = (state) => {
  return get(state, 'tools.commerceWizard.selectedRecommendation');
};

export const extractCommerceWizardSelectedPreviousRecommendation = (state) => {
  return get(state, 'tools.commerceWizard.selectedPreviousRecommendation');
};

export const extractCommerceWizardProductDetected = (state) => {
  const ticketId = extractSelectedTicketIdFromState(state);

  return get(state, `mlData.recommendedProducts.${ticketId}.productDetected`, null);
};

export const extractCommerceWizardCrossSellProducts = (state) => {
  const ticketId = extractSelectedTicketIdFromState(state);

  return get(state, `mlData.recommendedProducts.${ticketId}.crossSellProducts`, []) || [];
};

export const extractCommerceWizardDetectionSource = (state) => {
  const ticketId = extractSelectedTicketIdFromState(state);

  return get(state, `mlData.recommendedProducts.${ticketId}.detectionSource`, '');
};

export const extractCommerceWizardUpsSellProducts = (state) => {
  const ticketId = extractSelectedTicketIdFromState(state);

  return get(state, `mlData.recommendedProducts.${ticketId}.upSellProducts`, []) || [];
};

export const extractCommerceWizardGlobalProduct = (state) => {
  const ticketId = extractSelectedTicketIdFromState(state);

  return get(state, `mlData.recommendedProducts.${ticketId}.globalProducts`, []) || [];
};

export const extractConversationCommerceDataOfSelectedTicket = (state) => {
  const { conversationalCommerce } = extractSelectedTicketBusinessDetailsFromState(state) || {};

  return conversationalCommerce;
};

export const isCommerceWizardActive = createSelector(
  [extractCenterPaneInfoPanelActiveFromState, extractInfoPanelListTypeFromState],
  (wizardActive, infoPanelListType) => {
    return wizardActive && infoPanelListType === listTypes.COMMERCE;
  }
);

export const extractTabNotifications = (state) => state.notifications.tabNotificationsQueue || [];

export const extractFirstNominatedNotification = (state) => {
  const validNotification = state.notifications.tabNotificationsQueue.find((notification) => {
    return includes(NOMINATED_TICKET_STATUSES, notification.ticketStatus);
  });

  return validNotification ? extractTicketFromStateById(state, validNotification.ticketId) : null;
};

export const extractPreviouslyRecommendedProducts = _createSelector([extractSelectedTicketFromState], (ticket) => {
  return get(ticket, 'mlData.previouslyRecommendedProducts');
});

export const extractNominatedNotifications = _createSelector([extractTabNotifications], (tabNotifications) => {
  return tabNotifications.filter((notification) => {
    return includes(NOMINATED_TICKET_STATUSES, notification.ticketStatus);
  });
});

export const extractNominatedNotificationActive = _createSelector(
  [extractNominatedNotifications, getChatAlertActiveId],
  (nominatedNotifications, chatAlertActiveId) => {
    if (!!chatAlertActiveId && !!nominatedNotifications) {
      return nominatedNotifications.some((notification) => notification.ticketId === chatAlertActiveId);
    }

    return false;
  }
);

export const getEasyConnectionData = (state) => get(state, `mlData.easyConnection`);

export const extractEasyConnectionDataForSelectedTicket = _createSelector(
  [extractSelectedTicketIdFromState, getEasyConnectionData],
  (selectedTicketId, ecData) => {
    return get(ecData, selectedTicketId);
  }
);

export const getAgentAssistMacroRecommendation = (state) => get(state, `mlData.agentAssistMacroRecommendation`);

export const extractAgentAssistMacro = _createSelector(
  [getAgentAssistMacroRecommendation],
  (agentAssistMacroRecommendation) => get(agentAssistMacroRecommendation, 'details.agentAssistResponse', '')
);

export const extractAgentAssistSource = _createSelector(
  [getAgentAssistMacroRecommendation],
  (agentAssistMacroRecommendation) => get(agentAssistMacroRecommendation, 'details.source', '')
);

export const extractInProgressTreeAndWizardLocation = (state, ticketId) => {
  return get(state, `inProgressTreeAndWizardLocation.ticketIds.${ticketId}`);
};

/** *** Extractions used by ChatUI *****/

export const extractIncomingMessages = (state) => {
  return get(state, 'conversation.allMessages', []).filter(
    (messageId) => state.conversation[messageId].direction === DIRECTIONS.INBOUND
  );
};

export const extractOutgoingMessages = (state) => {
  return get(state, 'conversation.allMessages', []).filter(
    (messageId) => state.conversation[messageId].direction === DIRECTIONS.OUTBOUND
  );
};

export const extractNumberOfIncomingMessages = (state) => {
  return get(state, 'conversation.allMessages', []).filter(
    (messageId) => state.conversation[messageId].direction === DIRECTIONS.INBOUND
  ).length;
};

export const extractIncomingMessagesArray = _createSelector(
  [extractIncomingMessages, getConversation],
  (incomingMessageIds, conversation) => {
    const incomingMessages = [];
    for (const incomingMessageId of incomingMessageIds) {
      incomingMessages.push(conversation[incomingMessageId].messageBody);
    }

    return incomingMessages;
  }
);

export const extractOutgoingMessagesArray = _createSelector(
  [extractOutgoingMessages, getConversation],
  (incomingMessageIds, conversation) => {
    const incomingMessages = [];
    for (const incomingMessageId of incomingMessageIds) {
      incomingMessages.push(conversation[incomingMessageId].messageBody);
    }

    return incomingMessages;
  }
);

export const extractLastIncomingMessage = _createSelector(
  [extractIncomingMessages, getConversation],
  (incomingMessageIds, conversation) => {
    const lastMessageId = [...incomingMessageIds].pop();

    return conversation[lastMessageId];
  }
);

export const extractChatUIStatus = (state) => {
  // If for some reason the status object is not there in state, we should default to not show the chatUI at all
  return get(state, 'ui.chatUI.status', { isOpen: false, isOnscreen: false, activeTab: null });
};

export const extractChatUICopiedMacros = (state) => {
  return get(state, 'ui.chatUI.productRecMacroCopied', {});
};

export const extractChatUIMacroCopiedForSelectedTicket = _createSelector(
  [extractSelectedTicketIdFromState, extractChatUICopiedMacros],
  (selectedTicketId, chatCopiedMacros) => {
    if (!isEmpty(Object.keys(chatCopiedMacros))) {
      return Object.keys(chatCopiedMacros).includes(selectedTicketId);
    }

    return false;
  }
);

export const extractTicketFieldsForSelectedTicket = _createSelector(
  [extractSelectedTicketFromState],
  (selectedTicket) => {
    return get(selectedTicket, 'moreTicketInfo.ticketFields', []);
  }
);

export const getNumberCustomerSupportTransactions = (state) => {
  return state.tools?.ordersSearch?.numberCustomerSupportTransactions ?? 0;
};

export const getAdditionalOrderDetails = (state) => {
  return get(state, 'tools.ordersSearch.selectedOrder.additionalOrderDetails', {});
};

export const getRbiCustomerId = createSelector(
  [getRbiCustomerIdFromOrder, getRbiCustomerIdFromLoyaltyInfo, getLegacyCognitoId],
  (customerIdFromOrder, customerIdFromLoyaltyInfo, thLegacyCognitoId) =>
    customerIdFromOrder || customerIdFromLoyaltyInfo || thLegacyCognitoId
);

export const getCustomerDetails = (state) => state.tools.customerDetails;

export const getRestartDecisionFlowData = createSelector(
  [extractMacroWizardObject],
  (macroWizard) => macroWizard.restartDecisionFlowData
);

export const selectTicketLanguage = _createSelector([extractSelectedTicketFromState], (selectedTicket) => {
  return selectedTicket?.language || DEFAULT_CONVERSATION_LANGUAGE_CODE;
});

/**
 * Flag that indicates whether the current selected ticket|conversation contains
 * attributes related to the LLM Translation feature.
 */
export const selectIsTicketTranslated = _createSelector(
  [selectTicketLanguage, getAllMessages],
  (ticketLanguage, messages) => {
    const ticketIsInForeignLanguage = ticketLanguage !== DEFAULT_CONVERSATION_LANGUAGE_CODE;
    const messagesHaveDetectedLanguage = messages.some((message) => message.detectedLanguage);
    const messagesHaveTranslatedMessage = messages.some((message) => message.translatedMessage);

    return ticketIsInForeignLanguage && messagesHaveDetectedLanguage && messagesHaveTranslatedMessage;
  }
);

export const getFlowDebugStateFavoriteFieldsList = _createSelector(
  [extractSelectedTicketBusinessDetailsFromState],
  (businessDetails) => businessDetails.flowDebugStateFavoriteFieldsList
);

export const getFlowSystemLinkFromDebugState = (state) => {
  if (!getFlowDebugModeStatus(state)) {
    return null;
  }

  const messages = getAllMessages(state);
  const msg = messages.find((m) => m?.flowDebugState?.interaction?.context || m?.flowDebugState?.state?.flowSystem);

  if (!msg) {
    return null;
  }

  const messageContext = msg.flowDebugState.interaction?.context;
  const messageFlowSystem = msg.flowDebugState?.state?.flowSystem;
  const simplrAccountId = messageContext?.simplrAccountId || messageFlowSystem?.simplrAccountId;
  const flowSystemId = messageContext?.customFlowSystemId || messageFlowSystem?.id;

  if (!simplrAccountId || !flowSystemId) {
    return null;
  }

  return `${simplrAccountId}/flows/${flowSystemId}`;
};
